import {HttpClient} from '@angular/common/http';
import {computed, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Status} from '@domain/response-status';
import {UserType} from '@infrastructure/generic-auth/user-type.enum';
import {RequestStatus} from '@infrastructure/request-status.enum';
import {TranslateService} from '@ngx-translate/core';
import {TopBarService} from '@shared/top-bar/top-bar.service';
import {catchError, EMPTY, finalize, firstValueFrom, map, merge, of, Subject, switchMap, tap} from 'rxjs';
import {TokenResponse} from 'src/domain/token-response';
import {GenericAuthService} from 'src/infrastructure/generic-auth/generic-auth.service';
import {SnackBarService} from 'src/shared/snackbar/snackbar.service';
import {AdminService} from '../admin.service';
import {ClinicService} from '../clinics/clinic.service';

@Injectable({
    providedIn: 'root'
})
export class AdminAuthService
{
    private readonly httpClient = inject(HttpClient);

    private readonly genericAuthService = inject(GenericAuthService);

    private readonly topBarService = inject(TopBarService);

    private readonly snackBar = inject(SnackBarService);

    private readonly translate = inject(TranslateService);

    private readonly clinicService = inject(ClinicService);

    private readonly adminService = inject(AdminService);

    private readonly emailState = signal<Status>({
        status: RequestStatus.NOT_EXECUTED
    });

    private readonly state = signal<Status>({
        status: RequestStatus.NOT_EXECUTED
    });

    public readonly loadingEmailRequest = computed(() => this.emailState().status === RequestStatus.PENDING);

    public readonly errorOnEmailRequest = computed(() => this.emailState().status === RequestStatus.ERROR);

    private readonly login$ = new Subject<{email: string; password: string}>();
    
    private readonly signUp$ = new Subject<{email: string; password: string}>();

    private readonly clinicianSignup$ = new Subject<{email: string; password: string}>();

    private readonly verifyAccount$ = new Subject<{token: string}>();

    public readonly  loading = computed(() => this.state().status === RequestStatus.PENDING);
    
    public readonly showEmailSent = signal<boolean>(false);
    
    private readonly loggedIn$ = this.login$.pipe(
        switchMap(({email, password}) =>
        {
            return this.httpClient.post('admin/login', {email, password}).pipe(
                map((responseJSON: TokenResponse) => ({
                    accessToken: responseJSON.accessToken,
                    refreshToken: responseJSON.refreshToken
                })),
                catchError((error) =>
                {
                    this.snackBar.showErrorSnackbar(this.translate.instant(error?.error?.statusCode === 401 ?
                        'GENERAL.RESPONSE_ERRORS.ACCOUNT_BLOCKED' :
                        'GENERAL.RESPONSE_ERRORS.INVALID_EMAIL_OR_PASSWORD'), 4000);

                    this.state.set({
                        status: RequestStatus.ERROR
                    });
                
                    return EMPTY;
                })
            );
        })
    );

    private readonly signedUp$ = this.signUp$.pipe(
        switchMap(({email, password}) =>
        {
            return this.httpClient.post<unknown>('admin/signup', {email, password}).pipe(
                tap(() => this.showEmailSent.set(true)),
                catchError(() =>
                {
                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.GENERIC'));
                    
                    this.emailState.set({
                        status: RequestStatus.ERROR
                    });
    
                    return EMPTY;
                })
            );
        })
    );

    private readonly clinicianSignedup$ = this.clinicianSignup$.pipe(
        switchMap(({email, password}) =>
        {
            return this.httpClient.post<unknown>('admin/clinician-signup', {email, password}).pipe(
                tap(() => this.showEmailSent.set(true)),
                catchError(({error}) =>
                {

                    this.snackBar.showErrorSnackbar(this.translate.instant(`GENERAL.RESPONSE_ERRORS.${error.error === 'ALREADY_ACTIVE' ? 'ALREADY_ACTIVE' : 'GENERIC'}`));
                    
                    this.emailState.set({
                        status: RequestStatus.ERROR
                    });
    
                    return EMPTY;
                })
            );
        })
    );

    private readonly verifiedAccount$ = this.verifyAccount$.pipe(
        switchMap(({token}) =>
        {
            return this.httpClient.post<unknown>('admin/account-activation', {token}).pipe(
                catchError(() =>
                {
                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.GENERIC'));
                    this.emailState.set({
                        status: RequestStatus.ERROR
                    });
    
                    return EMPTY;
                })
            );
        })
    );

    public constructor()
    {
        this.loggedIn$.pipe(
            takeUntilDestroyed()
        ).subscribe({
            next: ({accessToken, refreshToken}) =>
            {
                this.genericAuthService.saveTokens(accessToken, refreshToken, UserType.ADMIN);

                this.adminService.getAdminDetails();

                this.clinicService.getClinics();

                this.topBarService.setShowClinicsDropdown(true);

                this.state.set({
                    status: RequestStatus.CONCLUDED
                });
            }
        });

        merge(this.signedUp$, this.verifiedAccount$, this.clinicianSignedup$).pipe(takeUntilDestroyed()).subscribe({
            next: () =>
            {
                this.emailState.set({
                    status: RequestStatus.CONCLUDED
                });
            }
        });
    }

    public login(email: string, password: string): void
    {
        this.state.set({
            status: RequestStatus.PENDING
        });

        this.login$.next({email, password});
    }

    public signup(email: string, password: string): void
    {
        this.emailState.set({
            status: RequestStatus.PENDING
        });

        this.signUp$.next({email, password});
    }

    public clinicianSignup(email: string, password: string): void
    {
        this.emailState.set({
            status: RequestStatus.PENDING
        });

        this.clinicianSignup$.next({email, password});
    }

    public verifyAccount(token: string): void
    {
        this.emailState.set({
            status: RequestStatus.PENDING
        });

        this.verifyAccount$.next({token});
    }

    public checkEmail(email: string): Promise<boolean>
    {
        this.state.set({
            status: RequestStatus.PENDING
        });

        return firstValueFrom(this.httpClient.get(`admin/check-email?email=${email}`).pipe(
            map(() => true),
            finalize(() => this.state.set({
                status: RequestStatus.CONCLUDED
            })),
            catchError(() =>
            {
                this.state.set({
                    status: RequestStatus.ERROR
                });
                    
                return of(false);
            })
        ));
    }

    public fetchAdminDetails(): void
    {
        this.adminService.getAdminDetails();

        this.clinicService.getClinics();

        this.topBarService.setShowClinicsDropdown(true);
    }
}
